import { useEffect } from "react";
import { Link } from "react-router-dom";
import CheckoutButton from "@/components/CheckoutButton";
import { OFFERS } from "@/lib/checkout";
import { track } from "@/lib/track";

export default function QuizResultView({ result }) {
  useEffect(() => {
    track("quiz_result_viewed", { track: result.track, score: result.score });
  }, [result]);

  const c = 2 * Math.PI * 64;
  const next = result.next_offer || {};
  const ctaLabel =
    next.kind === "offer" && OFFERS[next.lookup]
      ? `Explore the ${OFFERS[next.lookup].name} — ${OFFERS[next.lookup].price}`
      : "Book a Free Consultation";

  return (
    <div data-testid="quiz-result" className="mt-10">
      <div className="grid sm:grid-cols-[auto,1fr] gap-8 items-center rounded-3xl border border-gold/25 bg-obsidian-800/60 p-8">
        <div className="relative w-40 h-40 mx-auto shrink-0" data-testid="quiz-result-score">
          <svg viewBox="0 0 160 160" className="w-40 h-40 -rotate-90" aria-hidden="true">
            <circle cx="80" cy="80" r="64" stroke="#16283c" strokeWidth="10" fill="none" />
            <circle
              cx="80" cy="80" r="64" stroke="#D4AF37" strokeWidth="10" fill="none" strokeLinecap="round"
              strokeDasharray={c} strokeDashoffset={c * (1 - (result.score || 0) / 100)}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-4xl font-extrabold text-white">
              {result.score}<span className="text-lg text-slate-400">/100</span>
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500 mt-1">Self-reported</span>
          </div>
        </div>
        <div className="text-center sm:text-left">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Your result</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mt-2" data-testid="quiz-result-band">
            {result.band_label}
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed mt-3">{result.meaning}</p>
          {result.strongest_area && (
            <p className="text-slate-400 text-sm mt-3" data-testid="quiz-result-strongest">
              Reported strongest area: <span className="text-emerald-400 font-semibold">{result.strongest_area}</span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 rounded-3xl border border-slate-700/60 bg-obsidian-800/40 p-7" data-testid="quiz-result-gap">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Your highest-priority gap</p>
        <h3 className="font-display text-xl font-bold text-white mt-2">{result.priority_gap?.title}</h3>
        <p className="text-slate-300 text-sm leading-relaxed mt-3">
          <span className="text-gold font-semibold">Your first action:</span> {result.priority_gap?.action}
        </p>
      </div>

      <div className="mt-5 rounded-3xl border border-gold/25 bg-obsidian-900/60 p-7 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">One relevant next step</p>
        <p className="text-slate-200 text-sm leading-relaxed mt-3 max-w-xl mx-auto">{next.blurb}</p>
        <div className="mt-5">
          {next.kind === "offer" ? (
            <span onClick={() => track("quiz_offer_clicked", { offer: next.lookup })}>
              <CheckoutButton
                lookup={next.lookup}
                label={ctaLabel}
                testid="quiz-result-offer"
                className="rounded-full bg-gold text-obsidian font-display font-bold px-8 py-4 hover:bg-gold-bright transition-colors"
              />
            </span>
          ) : (
            <Link
              to="/intake?type=consultation"
              data-testid="quiz-result-consultation"
              onClick={() => track("quiz_offer_clicked", { offer: "consultation" })}
              className="inline-block rounded-full bg-gold text-obsidian font-display font-bold px-8 py-4 hover:bg-gold-bright transition-colors"
            >
              Book a Free Consultation
            </Link>
          )}
        </div>
      </div>

      <p className="text-slate-500 text-xs leading-relaxed mt-6 text-center" data-testid="quiz-result-disclaimer">
        {result.disclaimer}
      </p>
    </div>
  );
}
